import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

import { AnyID, LinkedItem, SnapSide } from '@types'

import { getMorphedItemWidth } from './projectItemOnSlabFaces'
import { getRinconeroRegletaMm } from './rinconeroGeometry'
import {
  getObbCenter,
  getSideFacing,
  getSideFacingSlab,
  getWallDragReleaseGapMm,
  isSlabId,
  setSnappedSide,
  ensureMutableSnappedSides,
  type SnappableSlab,
} from './snappedSides'
import { SNAP_SIDES, type SnapGroupNode } from './snapGroups'

/**
 * Reconstruye snappedSides desde la geometría para que `getSnapGroups` pueda trabajar solo
 * con el registro de caras.
 *
 * Hace falta porque snappedSides lo escriben collideItems y projectItemOnSlabFaces al
 * arrastrar, y hay items que nunca pasan por ahí: los de proyectos guardados antes de que
 * existiera el registro, y los que entran por `addProjectItemsToCurrentScene`, que les
 * cambia el id y deja las referencias apuntando a ids que ya no existen.
 *
 * Es una pasada de arranque, no un respaldo permanente: no toca lo que ya está registrado
 * (incluidas las paredes y tabiques de la cara trasera), solo limpia las referencias
 * muertas y rellena las caras que quedan libres. Idempotente: volver a llamarla sobre una
 * escena ya hidratada no cambia nada.
 */

/** Holgura máxima entre dos caras para darlas por pegadas */
const CONTACT_GAP_MM = 30
/** Solape mínimo a lo largo de la cara: rozarse por una esquina no es estar pegado */
const CONTACT_OVERLAP_MM = 20
/** Solape mínimo en altura: es lo que separa un alto de un bajo que caen en la misma vertical */
const VERTICAL_OVERLAP_MM = 10

type Contact = {
  a: SnapGroupNode
  b: SnapGroupNode
  sideA: SnapSide
  sideB: SnapSide
  gap: number
}

type NodeBox = {
  node: SnapGroupNode
  center: Vector3
  quaternion: Quaternion
  xAxis: Vector3
  zAxis: Vector3
  halfWidth: number
  halfThickness: number
  bottom: number
  top: number
  /** Saliente de la regleta: ensancha la holgura de contacto (rinconeros) */
  regleta: number
}

/**
 * @param items todos los objetos de la escena, para saber qué referencias siguen vivas
 * @param candidates items que participan en el registro de caras (visibles, sin frontales
 *        ni itemGroups: el llamador aplica el mismo filtro que use para agrupar)
 * @returns número de caras nuevas registradas
 */
type SceneLookup = {
  has: (id: AnyID) => boolean
  get?: (id: AnyID) => unknown
  set?: (id: AnyID, value: unknown) => void
  values?: () => Iterable<unknown>
}

export function rebuildSnappedSides(
  items: SceneLookup,
  candidates: Iterable<SnapGroupNode>,
): number {
  const nodes = [...candidates]
    .filter((node) => node?.id && node.matrix)
    .map((node) => {
      const thawed = ensureMutableSnappedSides(node)
      if (thawed !== node) items.set?.(node.id as AnyID, thawed)
      return thawed
    })

  // Referencias muertas: ids que se remapearon al importar, o vecinos ya borrados.
  for (const node of nodes) {
    const sides = node.snappedSides
    if (!sides) continue
    for (const side of SNAP_SIDES) {
      const otherId = sides[side]
      if (otherId && !items.has(otherId)) delete sides[side]
    }
  }

  const boxes = nodes.map(getNodeBox)

  // Un MBJ a ras de pared puede llegar aquí sin snappedWall: el arrastre se paró
  // en el vecino y no pasó por projectItemOnSlabFaces. Sin esa etiqueta no es
  // host de frontal y el tramo se corta, aunque geométricamente esté en la fila.
  // La OBB guardada en el item de pared es un punto vacío; la losa real es la
  // misma que pintan Wall2D/Wall3D (centro, ancho, grosor y rotación).
  const slabs = listSlabPoses(items)
  for (const box of boxes) attachFlushWall(box, items, slabs)
  for (const box of boxes) registerSlabSide(box, items)
  const contacts: Contact[] = []
  for (let i = 0; i < boxes.length; i += 1) {
    for (let j = i + 1; j < boxes.length; j += 1) {
      const contact = getContact(boxes[i], boxes[j])
      if (contact) contacts.push(contact)
    }
  }

  // De menor a mayor holgura: cuando dos items se disputan la misma cara, gana el que
  // está de verdad pegado y el otro se queda sin registrar.
  contacts.sort((first, second) => first.gap - second.gap)

  let registered = 0
  for (const contact of contacts) {
    const { a, b, sideA, sideB } = contact
    if (a.snappedSides?.[sideA] || b.snappedSides?.[sideB]) continue
    // Ya se conocen por otras caras (p.ej. una L registrada al arrastrar): no duplicar.
    if (SNAP_SIDES.some((side) => a.snappedSides?.[side] === b.id)) continue
    if (SNAP_SIDES.some((side) => b.snappedSides?.[side] === a.id)) continue
    setSnappedSide(a, sideA, b.id as AnyID)
    setSnappedSide(b, sideB, a.id as AnyID)
    registered += 1
  }

  return registered
}

type SlabPose = {
  id: AnyID
  type?: string
  x?: number
  z?: number
  width?: number
  thickness?: number
  rotation?: number
}

/**
 * Losa con la misma caja que actualizan Wall2D y Wall3D. El `boundingBox` del
 * item nace en un punto de tamaño cero y no sirve para medir el hueco.
 */
function listSlabPoses(items: SceneLookup): SlabPose[] {
  if (!items.values) return []
  const slabs: SlabPose[] = []
  for (const value of items.values()) {
    const slab = value as SlabPose
    if (!slab?.id || !isSlabId(slab.id)) continue
    if (!((slab.width ?? 0) > 1) || !((slab.thickness ?? 0) > 0)) continue
    slabs.push(slab)
  }
  return slabs
}

function slabPoseVolume(slab: SlabPose) {
  const halfWidth = (slab.width ?? 0) / 2
  const halfThickness = (slab.thickness ?? 0) / 2
  const center = new Vector3(slab.x ?? 0, 0, slab.z ?? 0)
  const rotation = new Matrix4().makeRotationFromEuler(
    new Euler(0, -(slab.rotation ?? 0) + Math.PI * 2, 0, 'XYZ'),
  )
  const obb = new OBB(center.clone(), new Vector3(halfWidth, 1, halfThickness))
  obb.rotation = new Matrix3().setFromMatrix4(rotation)
  const quat = new Quaternion().setFromRotationMatrix(rotation)
  return { obb, quat, center, halfWidth, halfThickness }
}

/**
 * Hueco (mm) hasta la cara habitable. En una pared de perímetro solo cuenta la
 * cara frontal (+Z local, la que mira a la habitación). Un tabique admite
 * cualquiera de sus caras.
 */
function gapToHabitableFace(box: NodeBox, slab: SlabPose): number {
  const { obb, quat, center, halfWidth, halfThickness } = slabPoseVolume(slab)
  const origin = new Vector3()
  const itemQuat = new Quaternion()
  box.node.matrix.decompose(origin, itemQuat, new Vector3())
  const gap = getWallDragReleaseGapMm(
    origin,
    itemQuat,
    box.halfWidth * 2,
    (box.node as LinkedItem).height || 0,
    box.halfThickness * 2,
    obb,
  )

  if (slab.type !== 'walls') return gap

  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quat)
  const delta = new Vector3(box.center.x - center.x, 0, box.center.z - center.z)
  // Dentro o detrás del muro: no es la fila de la habitación.
  if (zAxis.dot(delta) < halfThickness - 1) return Number.POSITIVE_INFINITY
  const along =
    box.halfWidth * Math.abs(box.xAxis.dot(xAxis)) +
    box.halfThickness * Math.abs(box.zAxis.dot(xAxis))
  if (Math.abs(xAxis.dot(delta)) > halfWidth + along) return Number.POSITIVE_INFINITY
  return gap
}

/**
 * Si el hueco entre el mueble y una losa cabe en la holgura de contacto, esa losa
 * es su pared. No mueve el mueble: solo escribe el mismo snappedWall que escribiría
 * el snap al arrastrar.
 */
function attachFlushWall(box: NodeBox, items: SceneLookup, slabs: SlabPose[]) {
  const node = box.node as LinkedItem
  const current = node.snappedWall as AnyID | undefined | null
  if (current && items.has(current)) return

  let bestId: AnyID | null = null
  let bestGap = CONTACT_GAP_MM
  for (const slab of slabs) {
    const gap = gapToHabitableFace(box, slab)
    if (gap <= bestGap) {
      bestGap = gap
      bestId = slab.id
    }
  }
  if (!bestId) return
  node.snappedWall = bestId
}

/**
 * Registra en el mueble la cara que mira a la losa a la que está pegado.
 * La cara sale del tramo de la losa: puede ser la trasera o un lateral de tabique.
 */
function registerSlabSide(box: NodeBox, items: { get?: (id: AnyID) => unknown }) {
  const slabId = (box.node as LinkedItem).snappedWall as AnyID | undefined | null
  if (!slabId || !items.get) return
  if (SNAP_SIDES.some((side) => box.node.snappedSides?.[side] === slabId)) return

  const slab = items.get(slabId) as SnappableSlab | undefined
  const side = getSideFacingSlab(box.quaternion, box.center, slab)
  if (side && !box.node.snappedSides?.[side]) setSnappedSide(box.node, side, slabId)
}

function getNodeBox(node: SnapGroupNode): NodeBox {
  const position = new Vector3()
  const quaternion = new Quaternion()
  node.matrix.decompose(position, quaternion, new Vector3())
  const width = getMorphedItemWidth(node)
  const height = node.height || 0
  const thickness = node.thickness || 0
  return {
    node,
    center: getObbCenter(position, quaternion, width, height, thickness),
    quaternion,
    xAxis: new Vector3(1, 0, 0).applyQuaternion(quaternion),
    zAxis: new Vector3(0, 0, 1).applyQuaternion(quaternion),
    halfWidth: width / 2,
    halfThickness: thickness / 2,
    bottom: position.y,
    top: position.y + height,
    // Saliente REAL: el valor guardado va invertido dentro del rango del morph (salvo en
    // KARB). Usarlo en crudo dejaba la holgura corta y el mueble apoyado en el frontal de
    // un rinconero no llegaba a contar como contacto.
    regleta: getRinconeroRegletaMm(node),
  }
}

/**
 * Cara por la que se tocan `a` y `b`, o `null` si no se tocan.
 *
 * Se proyecta el vector entre centros sobre los ejes locales de `a`: la cara buena es
 * aquella cuya separación cabe en la holgura mientras el otro eje solapa de verdad. Un
 * mueble contra el frontal de un rinconero queda separado por el saliente de la regleta,
 * así que ese saliente se suma a la holgura.
 */
function getContact(a: NodeBox, b: NodeBox): Contact | null {
  const verticalOverlap = Math.min(a.top, b.top) - Math.max(a.bottom, b.bottom)
  if (verticalOverlap <= VERTICAL_OVERLAP_MM) return null

  const delta = new Vector3(b.center.x - a.center.x, 0, b.center.z - a.center.z)
  const alongX = a.xAxis.dot(delta)
  const alongZ = a.zAxis.dot(delta)

  const bHalfOnAx =
    b.halfWidth * Math.abs(b.xAxis.dot(a.xAxis)) + b.halfThickness * Math.abs(b.zAxis.dot(a.xAxis))
  const bHalfOnAz =
    b.halfWidth * Math.abs(b.xAxis.dot(a.zAxis)) + b.halfThickness * Math.abs(b.zAxis.dot(a.zAxis))

  const gapX = Math.abs(alongX) - (a.halfWidth + bHalfOnAx)
  const gapZ = Math.abs(alongZ) - (a.halfThickness + bHalfOnAz)

  const maxGap = CONTACT_GAP_MM + Math.max(a.regleta, b.regleta)
  const touchesOnX = gapX <= maxGap && gapZ <= -CONTACT_OVERLAP_MM
  const touchesOnZ = gapZ <= maxGap && gapX <= -CONTACT_OVERLAP_MM
  if (!touchesOnX && !touchesOnZ) return null

  // Si los dos ejes valen (se solapan), la cara real es la del eje menos invadido.
  const onXFace = touchesOnX && (!touchesOnZ || gapX >= gapZ)
  const sideA: SnapSide = onXFace ? (alongX < 0 ? 'left' : 'right') : alongZ < 0 ? 'back' : 'front'

  return {
    a: a.node,
    b: b.node,
    sideA,
    sideB: getSideFacing(b.quaternion, b.center, a.center),
    gap: onXFace ? gapX : gapZ,
  }
}
